/**
 * S5 — ARC control scoping board (DC-1782 lineage; D-BANK-UX-02 production
 * rebuild of the PM-accepted Phase 1B hybrid).
 *
 * The reviewer's decision surface: three explicit lanes — SUGGESTED /
 * IN SCOPE / OUT OF SCOPE — fed by the entitled-catalog drawer. The visible
 * movement of a control from recommendation to reviewer-owned scope IS the
 * product; every movement persists through the audited mapping contract.
 *
 * Contract carried in the markup (scopeBoard.test.tsx pins each rule):
 *  - the complete workflow works without pointer drag: every card and drawer
 *    row exposes a Move/Add action (keyboard- and touch-operable);
 *  - drag is a pointer-event enhancement of the same operations, never a
 *    separate code path — a drop calls exactly the handler the menu calls;
 *  - exclusion demands a durable reason in a focus-trapped aria-modal
 *    dialog (Escape closes, focus returns to the invoking control);
 *  - undo is a PERSISTED revert (PATCH disposition:'pending' /
 *    prior decision), audited server-side like any other decision;
 *  - failed persistence never corrupts the board: state re-renders from the
 *    last confirmed payload, and the failure is announced;
 *  - the validation gate stays fail-closed while suggestions are undecided;
 *    validated scope re-opens on any change (server digest rule);
 *  - family (bundle) controls carry the FAMILY treatment and the
 *    counted-once statement — never double-counted;
 *  - identifiers-only boundary: control names render only when the entitled
 *    Library serves them; otherwise codes stand alone.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { ARC_IDENTIFIER_NOTE, ARC_SUBSET_LABEL } from './useCaseVocabulary';
import {
  FocusTrapDialog,
  useAnnouncer,
  useMediaFlag,
  usePointerDrag,
  useReducedMotion,
  type DragPayload,
} from './boardKit';
import './banking-use-cases.css';
import './banking-board.css';

// ============================================================================
// Payload types (server contract — unchanged shapes + arc-universe)
// ============================================================================

interface ArcTestMapping {
  id: string;
  testCode: string;
  evidenceChannel: string;
  disposition: string;
  exclusionReason: string | null;
  decidedAt: string | null;
}

interface ArcMapping {
  id: string;
  useCaseId: string;
  controlCode: string;
  scopeLevel: 'use_case' | 'bundle';
  bundleScope: string | null;
  catalogVersion: string;
  rulesetVersion: string;
  origin: 'suggested' | 'reviewer_added';
  rationale: string;
  citation: string;
  interpretiveNote: string | null;
  disposition: 'pending' | 'included' | 'excluded';
  exclusionReason: string | null;
  decidedAt: string | null;
  tests: ArcTestMapping[];
}

interface ValidationReason {
  code: string;
  message: string;
  count?: number;
}

interface ScopeStatus {
  state: string;
  reasons: ValidationReason[];
}

interface ScopeData {
  mappings: ArcMapping[];
  validation: { id: string; confirmedAt: string } | null;
  status: ScopeStatus;
  currentVersions: {
    catalogVersion: string;
    rulesetVersion: string;
    scopeLabel?: string;
    source?: 'provisional_subset' | 'entitled_catalog';
  };
}

interface UniverseTestRef {
  testCode: string;
  evidenceChannel: string;
}

interface UniverseControlRef {
  controlCode: string;
  scopeLevel: 'use_case' | 'bundle';
  bundleScope: string | null;
  citation: string;
  tests: UniverseTestRef[];
}

interface MappingUniverse {
  catalogVersion: string;
  rulesetVersion: string;
  scopeLabel: string;
  source: 'provisional_subset' | 'entitled_catalog';
  controls: UniverseControlRef[];
}

interface BrowseControl {
  controlCode: string;
  name: string;
}

interface BrowseUseCase {
  controls: BrowseControl[];
}

interface BrowseTree {
  useCases: BrowseUseCase[];
  bundleControls: BrowseControl[];
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'loaded'; scope: ScopeData };

type Lane = 'pending' | 'included' | 'excluded';

interface DialogTarget {
  mappingId: string;
  testMappingId?: string;
  label: string;
  trigger: HTMLElement | null;
}

interface MenuTarget {
  mappingId: string;
  trigger: HTMLElement | null;
}

interface UndoEntry {
  mappingId: string;
  label: string;
  priorDisposition: 'pending' | 'included' | 'excluded';
  priorReason: string | null;
}

export interface UseCaseArcScopeProps {
  useCaseId: string;
  onStatusChange?: (state: string) => void;
}

const LANE_LABELS: Record<Lane, string> = {
  pending: 'Suggested — awaiting your decision',
  included: 'In scope — will be assessed',
  excluded: 'Out of scope',
};

export const UseCaseArcScope: React.FC<UseCaseArcScopeProps> = ({
  useCaseId,
  onStatusChange,
}) => {
  const { get, post, patch } = useApiClient();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [universe, setUniverse] = useState<MappingUniverse | null>(null);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<MenuTarget | null>(null);
  const [dialogTarget, setDialogTarget] = useState<DialogTarget | null>(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean | null>(null);
  const [drawerQuery, setDrawerQuery] = useState('');

  const reducedMotion = useReducedMotion();
  const constrained = useMediaFlag('(max-width: 1439px)');
  const { announce, node: announcerNode } = useAnnouncer('scope-announcer');

  // ---------------------------------------------------------------- loading
  const load = useCallback(async () => {
    try {
      const res = await get<{ data?: ScopeData }>(`/api/use-cases/${useCaseId}/arc-mappings`);
      if (res.data) {
        setState({ phase: 'loaded', scope: res.data });
        onStatusChange?.(res.data.status.state);
      } else {
        setState({ phase: 'error' });
      }
    } catch {
      setState({ phase: 'error' });
    }
  }, [get, useCaseId, onStatusChange]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await get<{ data?: { universe?: MappingUniverse } }>(
          '/api/use-cases/arc-universe'
        );
        if (!cancelled && res.data?.universe) setUniverse(res.data.universe);
      } catch {
        // The drawer degrades to the mapped-controls view; expansion still
        // works through the server-validated add path.
      }
      try {
        // Names come ONLY from the entitled Library read surface; a 403
        // (not entitled) leaves the identifiers-only rendering in place.
        const res = await get<{ data?: { library?: BrowseTree } }>('/api/arc-library/browse');
        if (cancelled || !res.data?.library) return;
        const map = new Map<string, string>();
        for (const uc of res.data.library.useCases) {
          for (const control of uc.controls) map.set(control.controlCode, control.name);
        }
        for (const control of res.data.library.bundleControls) {
          map.set(control.controlCode, control.name);
        }
        setNames(map);
      } catch {
        /* not entitled — codes stand alone */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [get]);

  const scope = state.phase === 'loaded' ? state.scope : null;

  // ---------------------------------------------------------------- actions
  const act = useCallback(
    async (fn: () => Promise<unknown>, success: string): Promise<boolean> => {
      setBusy(true);
      setActionError(null);
      try {
        await fn();
        await load();
        announce(success);
        return true;
      } catch (error) {
        const message =
          (error as { body?: { error?: { message?: string } } }).body?.error?.message ??
          'The decision could not be saved. The board shows the last confirmed state.';
        setActionError(message);
        announce(message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [load, announce]
  );

  const decide = useCallback(
    async (
      mapping: ArcMapping,
      disposition: 'pending' | 'included' | 'excluded',
      exclusionReason?: string
    ) => {
      const prior: UndoEntry = {
        mappingId: mapping.id,
        label: mapping.controlCode,
        priorDisposition: mapping.disposition,
        priorReason: mapping.exclusionReason,
      };
      const ok = await act(
        () =>
          patch(`/api/use-cases/${useCaseId}/arc-mappings/${mapping.id}`, {
            disposition,
            ...(exclusionReason ? { exclusionReason } : {}),
          }),
        disposition === 'included'
          ? `${mapping.controlCode} moved into scope`
          : disposition === 'excluded'
            ? `${mapping.controlCode} moved out of scope — reason recorded`
            : `${mapping.controlCode} returned to suggested`
      );
      if (ok) setUndoStack((stack) => [...stack.slice(-11), prior]);
    },
    [act, patch, useCaseId]
  );

  const addControl = useCallback(
    (controlCode: string) =>
      act(
        () => post(`/api/use-cases/${useCaseId}/arc-mappings`, { controlCode }),
        `${controlCode} added to scope from the entitled Library`
      ),
    [act, post, useCaseId]
  );

  const suggest = useCallback(
    () =>
      act(
        () => post(`/api/use-cases/${useCaseId}/arc-mappings/suggest`, {}),
        'Suggested mapping generated'
      ),
    [act, post, useCaseId]
  );

  const validate = useCallback(
    () => act(() => post(`/api/use-cases/${useCaseId}/validation`, {}), 'Scope validated'),
    [act, post, useCaseId]
  );

  const decideTest = useCallback(
    (mappingId: string, testMappingId: string, disposition: 'included' | 'excluded', reason?: string) =>
      act(
        () =>
          patch(`/api/use-cases/${useCaseId}/arc-mappings/${mappingId}/tests/${testMappingId}`, {
            disposition,
            ...(reason ? { exclusionReason: reason } : {}),
          }),
        disposition === 'included' ? 'Test reinstated' : 'Test excluded — reason recorded'
      ),
    [act, patch, useCaseId]
  );

  const undo = useCallback(async () => {
    const entry = undoStack[undoStack.length - 1];
    if (!entry || !scope) return;
    const mapping = scope.mappings.find((m) => m.id === entry.mappingId);
    if (!mapping) return;
    setUndoStack((stack) => stack.slice(0, -1));
    await act(
      () =>
        patch(`/api/use-cases/${useCaseId}/arc-mappings/${entry.mappingId}`, {
          disposition: entry.priorDisposition,
          ...(entry.priorDisposition === 'excluded' && entry.priorReason
            ? { exclusionReason: entry.priorReason }
            : {}),
        }),
      `Undid the last decision on ${entry.label}`
    );
  }, [undoStack, scope, act, patch, useCaseId]);

  const requestExclusion = useCallback(
    (mapping: ArcMapping, trigger: HTMLElement | null) => {
      setExcludeReason(mapping.exclusionReason ?? '');
      setDialogTarget({ mappingId: mapping.id, label: mapping.controlCode, trigger });
    },
    []
  );

  const confirmExclusion = useCallback(() => {
    const target = dialogTarget;
    const reason = excludeReason.trim();
    if (!target || !reason || !scope) return;
    setDialogTarget(null);
    setExcludeReason('');
    if (target.testMappingId) {
      void decideTest(target.mappingId, target.testMappingId, 'excluded', reason);
      return;
    }
    const mapping = scope.mappings.find((m) => m.id === target.mappingId);
    if (mapping) void decide(mapping, 'excluded', reason);
  }, [dialogTarget, excludeReason, scope, decide, decideTest]);

  // ------------------------------------------------------------------- drag
  const onDrop = useCallback(
    (payload: DragPayload, lane: string) => {
      if (!scope || busy) return;
      if (payload.kind === 'drawer') {
        if (lane === 'included') void addControl(payload.code);
        return;
      }
      const mapping = scope.mappings.find((m) => m.id === payload.mappingId);
      if (!mapping || mapping.disposition === lane) return;
      if (lane === 'excluded') {
        requestExclusion(mapping, null);
      } else if (lane === 'included' || lane === 'pending') {
        void decide(mapping, lane);
      }
    },
    [scope, busy, addControl, decide, requestExclusion]
  );

  const { drag, dragHandleProps, registerLane } = usePointerDrag(
    onDrop,
    state.phase === 'loaded' && !busy
  );

  // ------------------------------------------------------------ derivations
  const lanes = useMemo(() => {
    const result: Record<Lane, ArcMapping[]> = { pending: [], included: [], excluded: [] };
    for (const mapping of scope?.mappings ?? []) {
      result[mapping.disposition]?.push(mapping);
    }
    for (const lane of Object.keys(result) as Lane[]) {
      result[lane].sort((a, b) =>
        a.scopeLevel === b.scopeLevel
          ? a.controlCode.localeCompare(b.controlCode)
          : a.scopeLevel === 'bundle'
            ? 1
            : -1
      );
    }
    return result;
  }, [scope]);

  const testsQueued = useMemo(
    () =>
      lanes.included.reduce(
        (sum, m) => sum + m.tests.filter((t) => t.disposition === 'included').length,
        0
      ),
    [lanes]
  );

  const mappedCodes = useMemo(
    () => new Map((scope?.mappings ?? []).map((m) => [m.controlCode, m.disposition])),
    [scope]
  );

  const drawerControls = useMemo(() => {
    const source: UniverseControlRef[] =
      universe?.controls ??
      (scope?.mappings ?? []).map((m) => ({
        controlCode: m.controlCode,
        scopeLevel: m.scopeLevel,
        bundleScope: m.bundleScope,
        citation: m.citation,
        tests: m.tests.map((t) => ({ testCode: t.testCode, evidenceChannel: t.evidenceChannel })),
      }));
    const q = drawerQuery.trim().toLowerCase();
    if (!q) return source;
    return source.filter(
      (c) =>
        c.controlCode.toLowerCase().includes(q) ||
        (names.get(c.controlCode) ?? '').toLowerCase().includes(q)
    );
  }, [universe, scope, drawerQuery, names]);

  const isEmpty = (scope?.mappings.length ?? 0) === 0;
  const canValidate = scope?.status.state === 'reviewer_unconfirmed';
  const validated = scope?.status.state === 'validated';
  const pendingCodes = lanes.pending.map((m) => m.controlCode);

  const drawerState: 'open' | 'collapsed' | 'overlay' = (() => {
    const open = drawerOpen ?? !constrained;
    if (!open) return 'collapsed';
    return constrained ? 'overlay' : 'open';
  })();

  const closeMenu = useCallback(
    (restoreFocus: boolean) => {
      setMenuFor((current) => {
        if (restoreFocus) current?.trigger?.focus();
        return null;
      });
    },
    []
  );

  // ---------------------------------------------------------------- renders
  if (state.phase === 'loading') {
    return (
      <div className="banking-arc-scope" aria-busy="true">
        <div className="banking-grid-skeleton">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="banking-skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (state.phase === 'error' || !scope) {
    return (
      <div className="banking-arc-scope">
        <div className="banking-grid-error" role="alert">
          <p>The ARC mapping could not be loaded.</p>
          <button
            type="button"
            className="banking-btn banking-btn-secondary"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  function controlName(code: string): string | null {
    return names.get(code) ?? null;
  }

  function laneOfDrag(): string | null {
    return drag?.overLane ?? null;
  }

  function mappingCard(mapping: ArcMapping): React.ReactElement {
    const name = controlName(mapping.controlCode);
    const isFamily = mapping.scopeLevel === 'bundle';
    const isOpen = expanded.has(mapping.id);
    const menuOpen = menuFor?.mappingId === mapping.id;
    const includedTests = mapping.tests.filter((t) => t.disposition === 'included').length;
    const provenance =
      mapping.origin === 'suggested'
        ? `catalog ${mapping.catalogVersion} · ruleset ${mapping.rulesetVersion} · deterministic rule`
        : `catalog ${mapping.catalogVersion} · ruleset ${mapping.rulesetVersion} · added by reviewer`;
    return (
      <article
        key={mapping.id}
        data-testid={`scope-card-${mapping.controlCode}`}
        className={`scope-card lane-${mapping.disposition}${
          drag?.payload.mappingId === mapping.id ? ' drag-source' : ''
        }`}
        tabIndex={0}
        aria-label={`${mapping.controlCode}${name ? ` ${name}` : ''}, ${
          mapping.disposition === 'pending'
            ? 'suggested, awaiting decision'
            : mapping.disposition === 'included'
              ? 'in scope'
              : 'out of scope'
        }`}
        onKeyDown={(event) => {
          if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
            event.preventDefault();
            setMenuFor({ mappingId: mapping.id, trigger: event.currentTarget });
          }
          if (event.key === 'Escape' && menuOpen) closeMenu(true);
        }}
        {...dragHandleProps({
          kind: 'card',
          code: mapping.controlCode,
          mappingId: mapping.id,
          label: mapping.controlCode,
        })}
      >
        <header className="scope-card-head">
          <span className="scope-grip" aria-hidden="true">
            ⠿
          </span>
          <span className="scope-code">{mapping.controlCode}</span>
          {name ? (
            <span className="scope-name">{name}</span>
          ) : (
            <span className="scope-provisional" title={ARC_IDENTIFIER_NOTE}>
              Provisional · {mapping.catalogVersion}
            </span>
          )}
          {isFamily && (
            <span className="scope-family-badge" data-testid={`family-badge-${mapping.controlCode}`}>
              FUNCTION
            </span>
          )}
          <button
            type="button"
            className="scope-move-btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Move ${mapping.controlCode}`}
            disabled={busy || validated}
            onClick={(event) => {
              event.stopPropagation();
              setMenuFor(
                menuOpen ? null : { mappingId: mapping.id, trigger: event.currentTarget }
              );
            }}
          >
            Move ▾
          </button>
        </header>
        {menuOpen && (
          <div
            className="scope-menu"
            role="menu"
            aria-label={`Move ${mapping.controlCode} to`}
            ref={(el) => {
              // Focus enters the menu on open — keyboard users operate it
              // directly (Enter on the card, Enter on an item).
              if (el && !el.contains(document.activeElement)) {
                el.querySelector<HTMLButtonElement>('button')?.focus();
              }
            }}
            onKeyDown={(event) => {
              const items = Array.from(
                event.currentTarget.querySelectorAll<HTMLButtonElement>('button')
              );
              const index = items.indexOf(document.activeElement as HTMLButtonElement);
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const next =
                  event.key === 'ArrowDown'
                    ? (index + 1) % items.length
                    : (index - 1 + items.length) % items.length;
                items[next]?.focus();
              }
              if (event.key === 'Escape') {
                event.stopPropagation();
                closeMenu(true);
              }
            }}
          >
            {mapping.disposition !== 'included' && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeMenu(false);
                  void decide(mapping, 'included');
                }}
              >
                Move to In scope — include in the assessment
              </button>
            )}
            {mapping.disposition !== 'excluded' && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  const trigger = menuFor?.trigger ?? null;
                  closeMenu(false);
                  requestExclusion(mapping, trigger);
                }}
              >
                Move to Out of scope… (reason required)
              </button>
            )}
            {mapping.disposition !== 'pending' && mapping.origin === 'suggested' && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeMenu(false);
                  void decide(mapping, 'pending');
                }}
              >
                Return to suggested
              </button>
            )}
            <button type="button" role="menuitem" onClick={() => closeMenu(true)}>
              Cancel
            </button>
          </div>
        )}
        {mapping.disposition === 'pending' && (
          <p className="scope-sub scope-sub-suggested">{mapping.rationale}</p>
        )}
        {mapping.origin === 'reviewer_added' && mapping.disposition !== 'pending' && (
          <p className="scope-sub">Added by the reviewer from the entitled Library</p>
        )}
        <p className="scope-prov">{provenance}</p>
        <p className="scope-citation">Regulatory basis: {mapping.citation}</p>
        {mapping.interpretiveNote && (
          <p className="scope-sub" role="note">
            {mapping.interpretiveNote}
          </p>
        )}
        <div className="scope-chips" aria-label={`${mapping.controlCode} evidence channels`}>
          {[...new Set(mapping.tests.map((t) => t.evidenceChannel))].map((channel) => (
            <span key={channel} className={`scope-channel channel-${channel}`}>
              {channel}
            </span>
          ))}
        </div>
        {isFamily && (
          <p className="scope-family-note">
            Function-level — assessed once for{' '}
            {mapping.bundleScope ?? 'the function'} and counted once in every roll-up; a shared
            result is referenced by member use cases, never re-derived.
          </p>
        )}
        {mapping.disposition === 'excluded' && mapping.exclusionReason && (
          <p className="scope-exclusion">
            <strong>Out of scope — reviewer:</strong> {mapping.exclusionReason}
          </p>
        )}
        <div className="scope-card-foot">
          <span className="scope-tests-count">
            {mapping.tests.length > 0
              ? `${includedTests} of ${mapping.tests.length} tests in scope`
              : 'no measurable tests — evidence by attestation'}
          </span>
          {mapping.tests.length > 0 && (
            <button
              type="button"
              className="banking-link-btn"
              aria-expanded={isOpen}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(mapping.id)) next.delete(mapping.id);
                  else next.add(mapping.id);
                  return next;
                });
              }}
            >
              {isOpen ? 'Hide' : 'Inspect'} tests &amp; evidence
            </button>
          )}
        </div>
        {isOpen && (
          <ul className="scope-test-list" aria-label={`${mapping.controlCode} evidence legs`}>
            {mapping.tests.map((test) => (
              <li key={test.id} className={`scope-test test-${test.disposition}`}>
                <span className="scope-test-code">{test.testCode}</span>
                <span className={`scope-channel channel-${test.evidenceChannel}`}>
                  {test.evidenceChannel}
                </span>
                {test.disposition === 'excluded' ? (
                  <button
                    type="button"
                    className="banking-link-btn"
                    disabled={busy || validated}
                    onClick={() => void decideTest(mapping.id, test.id, 'included')}
                  >
                    Reinstate {test.testCode}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="banking-link-btn"
                    disabled={busy || validated}
                    onClick={(event) => {
                      setExcludeReason('');
                      setDialogTarget({
                        mappingId: mapping.id,
                        testMappingId: test.id,
                        label: test.testCode,
                        trigger: event.currentTarget,
                      });
                    }}
                  >
                    Exclude {test.testCode}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </article>
    );
  }

  function laneSection(lane: Lane): React.ReactElement {
    const items = lanes[lane];
    const hot = laneOfDrag() === lane;
    const dropLegal =
      drag &&
      (drag.payload.kind === 'card'
        ? true
        : lane === 'included'); /* drawer rows only drop into scope */
    return (
      <section
        key={lane}
        data-testid={`scope-lane-${lane}`}
        data-lane={lane}
        ref={(el) => registerLane(lane, el)}
        className={`scope-lane scope-lane-${lane}${hot && dropLegal ? ' drop-hover' : ''}`}
        role="group"
        aria-label={LANE_LABELS[lane]}
      >
        <header className="scope-lane-head">
          <span className="scope-lane-title">{LANE_LABELS[lane]}</span>
          <span className="scope-lane-count">
            {lane === 'included'
              ? `${items.length} controls · ${testsQueued} tests`
              : `${items.length}`}
          </span>
        </header>
        {lane === 'pending' && items.length > 0 && (
          <p className="scope-lane-hint">
            Proposed from the declared category. Move right to accept — or park out of scope
            with a reason.
          </p>
        )}
        {lane === 'excluded' && (
          <p className="scope-lane-hint">
            Every exclusion is recorded with the reviewer and reason — audit-ready.
          </p>
        )}
        <div className="scope-lane-body">
          {items.map(mappingCard)}
          {hot && dropLegal && drag && (
            <div className="scope-dropslot" aria-hidden="true">
              {lane === 'excluded'
                ? `Park ${drag.payload.label} out of scope — a reason will be required`
                : lane === 'included'
                  ? `Drop to include ${drag.payload.label} in the assessment`
                  : `Return ${drag.payload.label} to suggested`}
            </div>
          )}
          {items.length === 0 && !hot && (
            <p className="scope-lane-empty">
              {lane === 'pending'
                ? 'No undecided suggestions.'
                : lane === 'included'
                  ? 'Nothing in scope yet — move a suggested control here to accept it.'
                  : 'No exclusions.'}
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <div
      className="scope-board banking-arc-scope"
      data-testid="scope-board-root"
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      data-drawer={drawerState}
    >
      <header className="scope-board-head">
        <p className="scope-subset-note" role="note">
          {scope.currentVersions.scopeLabel ?? ARC_SUBSET_LABEL}. {ARC_IDENTIFIER_NOTE}
        </p>
        <div className="scope-counts" aria-hidden="true">
          <span>
            <b>{lanes.pending.length}</b> suggested
          </span>
          <span>
            <b>{lanes.included.length}</b> in scope
          </span>
          <span>
            <b>{lanes.excluded.length}</b> out
          </span>
          <span>
            <b>{testsQueued}</b> tests queued
          </span>
        </div>
      </header>

      {actionError && (
        <div className="banking-grid-error scope-action-error" role="alert">
          <p>{actionError}</p>
        </div>
      )}

      {isEmpty ? (
        <div className="banking-grid-empty" data-testid="arc-scope-empty">
          <h2>Map this use case to ARC</h2>
          {scope.status.reasons.map((reason) => (
            <p key={reason.code}>{reason.message}</p>
          ))}
          <p>
            Compass proposes a deterministic baseline from the use case&apos;s declared
            category. A reviewer restricts or expands it — testing only ever runs against a
            human-confirmed control set.
          </p>
          <button
            type="button"
            className="banking-btn banking-btn-primary"
            disabled={busy}
            onClick={() => void suggest()}
          >
            Generate suggested mapping
          </button>
        </div>
      ) : (
        <>
          <div className="scope-main">
            <aside className="scope-drawer" data-testid="arc-drawer">
              <button
                type="button"
                className="scope-drawer-toggle"
                aria-expanded={drawerState !== 'collapsed'}
                aria-controls="scope-drawer-body"
                onClick={() => setDrawerOpen(drawerState === 'collapsed')}
              >
                <span aria-hidden="true">{drawerState === 'collapsed' ? '»' : '«'}</span> ARC
                Library
                {universe && (
                  <span className="scope-drawer-count">{universe.controls.length} controls</span>
                )}
              </button>
              {drawerState !== 'collapsed' && (
                <div id="scope-drawer-body" className="scope-drawer-body">
                  <input
                    type="search"
                    className="scope-drawer-search"
                    aria-label="Search the entitled catalog"
                    placeholder="Search controls…"
                    value={drawerQuery}
                    onChange={(event) => setDrawerQuery(event.target.value)}
                  />
                  <ul className="scope-drawer-list">
                    {drawerControls.map((control) => {
                      const mapped = mappedCodes.get(control.controlCode);
                      const name = controlName(control.controlCode);
                      return (
                        <li
                          key={control.controlCode}
                          data-testid={`drawer-row-${control.controlCode}`}
                          className={`scope-drawer-row${mapped ? ' onboard' : ''}`}
                          {...(mapped
                            ? {}
                            : dragHandleProps({
                                kind: 'drawer',
                                code: control.controlCode,
                                label: control.controlCode,
                              }))}
                        >
                          <span className="scope-code">{control.controlCode}</span>
                          <span className="scope-drawer-name">
                            {name ?? (control.bundleScope ?? '')}
                          </span>
                          {control.scopeLevel === 'bundle' && (
                            <span className="scope-family-badge">FUNCTION</span>
                          )}
                          {mapped ? (
                            <span className="scope-drawer-state">
                              {mapped === 'pending' ? 'SUGG' : mapped === 'included' ? 'IN' : 'OUT'}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="banking-link-btn"
                              disabled={busy || validated}
                              aria-label={`Add ${control.controlCode} to scope`}
                              onClick={() => void addControl(control.controlCode)}
                            >
                              Add
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="scope-drawer-foot">
                    Content: ARC — A. Philo · legs &amp; triggers: ROSI overlay (provisional).
                    Drag a control into the board, or use Add.
                  </p>
                </div>
              )}
            </aside>

            <div className="scope-lanes">
              {laneSection('pending')}
              {laneSection('included')}
              {laneSection('excluded')}
            </div>
          </div>

          <section className="scope-gate-row" aria-label="Validation gate">
            {undoStack.length > 0 && !validated && (
              <button
                type="button"
                className="scope-undo"
                data-testid="scope-undo"
                disabled={busy}
                onClick={() => void undo()}
              >
                Undo — {undoStack[undoStack.length - 1].label}
              </button>
            )}
            <p className="scope-gate" data-testid="scope-gate" role="status">
              {validated
                ? `Scope validated${
                    scope.validation
                      ? ` ${new Date(scope.validation.confirmedAt).toLocaleDateString()}`
                      : ''
                  } — ${testsQueued} tests queued. Changing the mapping re-opens the gate.`
                : lanes.pending.length > 0
                  ? `${lanes.pending.length} undecided suggestion${
                      lanes.pending.length > 1 ? 's' : ''
                    } — decide ${pendingCodes.join(', ')} before validating.`
                  : scope.status.reasons.map((r) => r.message).join(' ') ||
                    'Every suggestion is decided — the scope can be validated.'}
            </p>
            <span className="scope-kbd-hint">Enter on a card opens its Move menu</span>
            {!validated && (
              <button
                type="button"
                className="banking-btn banking-btn-primary"
                data-testid="scope-validate"
                disabled={!canValidate || busy}
                onClick={() => void validate()}
              >
                Validate scope
              </button>
            )}
          </section>

          <section className="banking-arc-run scope-run" aria-label="Assessment run">
            <button type="button" className="banking-btn banking-btn-primary" disabled>
              Run assessment
            </button>
            <p className="banking-arc-run-reason">
              {validated
                ? 'Assessment execution arrives with the ARC assessment engine (DC-1740 line). No run can be created yet.'
                : 'Nothing runs until this scope is validated. The blockers above are the path.'}
            </p>
          </section>
        </>
      )}

      {drag && (
        <div
          className="scope-drag-proxy"
          style={{ left: drag.x - 130, top: drag.y - 20 }}
          aria-hidden="true"
        >
          <span className="scope-code">{drag.payload.code}</span>{' '}
          {controlName(drag.payload.code) ?? ''}
        </div>
      )}

      {dialogTarget && (
        <FocusTrapDialog
          label="Exclude from scope"
          restoreFocusTo={dialogTarget.trigger}
          onClose={() => {
            setDialogTarget(null);
            setExcludeReason('');
          }}
        >
          <h3 className="banking-modal-title">Park {dialogTarget.label} out of scope</h3>
          <p className="banking-modal-sub">
            An exclusion reason is required and becomes part of the assessment&apos;s audit
            record.
          </p>
          <textarea
            aria-label="Exclusion reason"
            value={excludeReason}
            onChange={(event) => setExcludeReason(event.target.value)}
            rows={3}
            placeholder="Why is this not being assessed for this use case?"
          />
          <div className="banking-modal-actions">
            <button
              type="button"
              className="banking-btn banking-btn-secondary"
              onClick={() => {
                setDialogTarget(null);
                setExcludeReason('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="banking-btn banking-btn-primary scope-exclude-confirm"
              disabled={!excludeReason.trim() || busy}
              onClick={confirmExclusion}
            >
              Record exclusion
            </button>
          </div>
        </FocusTrapDialog>
      )}

      {announcerNode}
    </div>
  );
};

export default UseCaseArcScope;
